<template>
    <v-app
        :class="classList"
        :style="{
            '--v-app-bar-height': '6rem',
            '--v-app-bar-offset': appBarOffset,
            '--v-app-breadcrumbs-height': appBreadcrumbsHeight,
            '--v-app-compatibility-warning-height': appCompatibilityWarningHeight,
            '--v-app-risk-warning-height': appRiskWarningHeight,
            '--v-app-investor-additional-needs-height': appInvestorAdditionalNeedsHeight,
            '--v-app-investor-portfolio-warning-height': appInvestorPortfolioWarningHeight,
            '--v-container-height': containerHeight,
            '--v-app-bar-top': appBarTop,
            '--v-app-main-padding-top': appMainPaddingTop
        }"
    >
        <app-menu-panel v-if="displayApp && $vuetify.breakpoint.mobile" />
        <app-snack-queue v-if="!blockPlatformUi" />
        <app-dialog-queue v-if="!blockPlatformUi" />
        <app-filter-panels v-if="!blockPlatformUi" />
        <app-media-library v-if="!blockPlatformUi" />

        <template v-if="displayApp">
            <v-snackbar
                v-if="$store.getters['app/mock_api']"
                :value="showMockingApiAlert"
                :timeout="-1"
                absolute
                centered
                top
                :elevation="2"
                color="primaryText"
                style="z-index: 8"
            >
                <template v-if="isStaging()">This is a staging environment with a mocked API</template>
                <template v-else>This is a development environment with a mocked API</template>

                <template #action>
                    <app-button icon="close" color="white" small @click="showMockingApiAlert = false" />
                </template>
            </v-snackbar>

            <app-compatibility-warning
                v-if="showCompatibilityWarning"
                @resize="$event => (appCompatibilityWarningHeight = $event.height)"
            />
            <app-risk-warning v-if="showRiskWarning" @resize="$event => (appRiskWarningHeight = $event.height)" />
            <app-bar v-if="showAppBar" />
            <app-investor-additional-needs
                v-if="showInvestorAdditionalNeeds"
                @resize="$event => (appInvestorAdditionalNeedsHeight = $event.height)"
            />
            <app-investor-portfolio-warning
                v-if="showInvestorPortfolioWarning"
                @resize="$event => (appInvestorPortfolioWarningHeight = $event.height)"
            />
            <app-breadcrumbs v-if="showCrumbs" @resize="$event => (appBreadcrumbsHeight = $event.height)" />
            <v-main class="app-main" :class="{ 'app-main--logged-in': showAppBar }">
                <transition appear name="fade" mode="out-in">
                    <router-view />
                </transition>

                <app-dialog
                    v-model="recategorised"
                    size="large"
                    :persistent="false"
                    :cancel-button-text="$t('proceed')"
                    hide-continue-button
                    :on-cancel-fn="() => (recategorised = false)"
                    :title="$t('thank_you_for_completing_your_categorisation')"
                    :overline="$t('categorisation')"
                >
                    <v-card-text class="px-6">
                        {{ $t('were_required_to_revalidate_this_information_with_you_every_12_months') }}
                    </v-card-text>
                </app-dialog>

                <app-user-feedback />
                <app-platform-modal />
                <app-progress-queue />
                <app-terms-modal
                    v-if="!Auth().onboarding"
                    v-model="requireTermsAcceptance"
                    @accepted="
                        $router.replace({
                            path: $route.path,
                            query: { ...$route.query, tcs: undefined }
                        });
                        replaceQueryParam('tcs', null);
                    "
                />

                <app-session-expired v-model="sessionExpired" />
            </v-main>
        </template>

        <app-dialog v-else-if="displayAccountSwitcher" persistent hide-actions :title="$t('please_select_an_account')">
            <v-card-text>
                <app-account-switcher-list />
            </v-card-text>
        </app-dialog>

        <app-platform-load-error v-else-if="platformLoadTimedOut" :diagnostics="platformLoadDiagnostics" />

        <v-overlay
            v-if="showPlatformLoadingOverlay"
            :value="true"
            color="background"
            opacity="1"
            style="z-index: 200"
        >
            <v-progress-circular
                :size="100"
                :width="7"
                :color="Auth().branding().colour('primaryBackground')"
                indeterminate
            />
        </v-overlay>

        <app-loading v-if="!blockPlatformUi" />
    </v-app>
</template>

<script>
import { isBrowserCompatible, isBrowserVersionCompatible } from '@core/router/middleware/checkBrowserCompatibility';
import ErrorService from '@core/services/ErrorService';

import { isStaging } from '@utils/env';
import { isPlatformLoadHangEnabled, getPlatformLoadTimeoutMs } from '@utils/platformLoadHang';
import { buildPlatformLoadDiagnostics } from '@utils/platformLoadDiagnostics';

import freshdesk from '@/mixins/freshdesk';
import config from '@/config';

export default {
    name: 'App',
    components: {
        AppBar: () => import('@/components/AppBar.vue'),
        AppBreadcrumbs: () => import('@/components/AppBreadcrumbs.vue'),
        AppRiskWarning: () => import('@/components/AppRiskWarning.vue'),
        AppCompatibilityWarning: () => import('@/components/AppCompatibilityWarning.vue'),
        AppAccountSwitcherList: () => import('@/components/AppAccountSwitcherList.vue'),
        AppFilterPanels: () => import('@/components/AppFilterPanels.vue'),
        AppSnackQueue: () => import('@/components/AppSnackQueue.vue'),
        AppDialog: () => import('@/components/AppDialog.vue'),
        AppDialogQueue: () => import('@/components/AppDialogQueue.vue'),
        AppMenuPanel: () => import('@/components/AppMenuPanel.vue'),
        AppMediaLibrary: () => import('@/components/AppMediaLibrary.vue'),
        AppUserFeedback: () => import('@/components/AppUserFeedback.vue'),
        AppSessionExpired: () => import('@/components/AppSessionExpired.vue'),
        AppLoading: () => import('@/components/AppLoading.vue'),
        AppPlatformModal: () => import('@/components/AppPlatformModal.vue'),
        AppProgressQueue: () => import('@/components/AppProgressQueue.vue'),
        AppTermsModal: () => import('@/components/AppTermsModal.vue'),
        AppInvestorPortfolioWarning: () => import('@/components/AppInvestorPortfolioWarning.vue'),
        AppInvestorAdditionalNeeds: () => import('@/components/AppInvestorAdditionalNeeds.vue'),
        AppPlatformLoadError: () => import('@/components/AppPlatformLoadError.vue')
    },
    mixins: [freshdesk],
    data: () => ({
        recategorised: false,
        requireTermsAcceptance: false,
        sessionExpired: false,
        showMockingApiAlert: true,
        isStaging,
        previous: {},
        pinging: false,

        appCompatibilityWarningHeight: '0px',
        appRiskWarningHeight: '0px',
        appBreadcrumbsHeight: '0px',
        appInvestorAdditionalNeedsHeight: '0px',
        appInvestorPortfolioWarningHeight: '0px',
        platformLoadTimedOut: false,
        platformLoadStartedAt: null,
        platformLoadDiagnostics: null,
        platformLoadTimer: null,
        platformLoadHangActive: isPlatformLoadHangEnabled()
    }),
    computed: {
        classList() {
            let classList = [];

            if (this.showRiskWarning) {
                classList.push('app--risk-warning');
            }

            if (this.showCompatibilityWarning) {
                classList.push('app--compatibility-warning');
            }

            return classList.join(' ');
        },
        isPublic() {
            return this.$route.meta.public === true;
        },
        showApp() {
            if (this.isPublic) {
                return true;
            }

            if (this.Auth().onboarding) {
                return true;
            }

            if (this.Auth().is_logged_in && this.Auth().is_initialised && this.Auth().has_branding) {
                return true;
            }

            return false;
        },
        simulatePlatformLoadHang() {
            if (this.platformLoadTimedOut) {
                return false;
            }

            return this.platformLoadHangActive || isPlatformLoadHangEnabled(this.$route);
        },
        blockPlatformUi() {
            return this.simulatePlatformLoadHang || this.platformLoadTimedOut;
        },
        displayApp() {
            if (this.blockPlatformUi) {
                return false;
            }

            return this.showApp;
        },
        displayAccountSwitcher() {
            if (this.blockPlatformUi) {
                return false;
            }

            return this.showAccountSwitcher;
        },
        showPlatformLoadingOverlay() {
            if (this.platformLoadTimedOut) {
                return false;
            }

            if (this.simulatePlatformLoadHang) {
                return true;
            }

            return !this.showApp && !this.showAccountSwitcher;
        },
        platformLoadTimeoutMs() {
            return getPlatformLoadTimeoutMs(this.$route, config.APP_PLATFORM_LOAD_TIMEOUT);
        },
        showAppBar() {
            if (this.$route.name === 'FatalError') {
                return false;
            }

            if (this.$route.name === 'PageError') {
                return false;
            }

            if (this.Auth().is_logged_in && this.Auth().is_initialised) {
                return true;
            }

            return false;
        },
        selectedInvestor() {
            return this.$store.getters['app/query_selector_data']('investor_id');
        },
        someInvestorsHavePortfolioRestrictions() {
            return (
                this.isAdviser() &&
                this.getAuthAdviser() &&
                this.getAuthAdviser().some_investors_have_portfolio_restrictions
            );
        },
        showInvestorAdditionalNeeds() {
            if (!this.showApp) {
                return false;
            }

            let result = false;

            if (this.isAdviser() && this.selectedInvestor && this.$route.name !== 'SettingsAdditionalNeeds') {
                result = this.selectedInvestor.has_additional_needs;
            }

            if (result !== this.previous.showInvestorAdditionalNeeds && !this.$store.getters['app/routing']) {
                return result;
            } else if (result && this.$store.getters['app/routing']) {
                return true;
            }

            return result;
        },
        showInvestorPortfolioWarning() {
            if (!this.showApp) {
                return false;
            }

            let result = false;

            if (this.isInvestor()) {
                const investor = this.getAuthInvestor();
                result = investor && investor.is_portfolio_warning;
            } else if (this.isAdviser() && this.selectedInvestor && !this.$route.path.startsWith('/library')) {
                result =
                    this.selectedInvestor.is_portfolio_warning ||
                    (this.selectedInvestor.is_portfolio_restricted &&
                        this.$route.name !== 'AdviserInvestorPortfolioRestrictedNotice');
            } else if (this.someInvestorsHavePortfolioRestrictions && this.$route.path === '/') {
                result = true;
            } else if (this.someInvestorsHavePortfolioRestrictions && this.$route.path === '/portfolio/dashboard') {
                result = !this.selectedInvestor;
            } else if (
                this.someInvestorsHavePortfolioRestrictions &&
                this.$route.path.startsWith('/reports') &&
                !this.$route.path.includes('activity')
            ) {
                result = !this.selectedInvestor;
            }

            if (result !== this.previous.showInvestorPortfolioWarning && !this.$store.getters['app/routing']) {
                return result;
            } else if (result && this.$store.getters['app/routing']) {
                return true;
            }

            return result;
        },
        showRiskWarning() {
            return (
                this.showAppBar &&
                this.$route &&
                this.$route.name &&
                this.$route.name.startsWith('Offer') &&
                this.isInvestor()
            );
        },
        showCompatibilityWarning() {
            return !isBrowserCompatible() || !isBrowserVersionCompatible();
        },
        showCrumbs() {
            return this.showAppBar && this.$store.getters['preferences/show_breadcrumbs'];
        },
        showAccountSwitcher() {
            if (this.Auth().is_logged_in && !this.Auth().is_initialised) {
                return true;
            }

            return false;
        },
        containerHeight() {
            let height = '100vh';

            if (this.showAppBar) {
                height = `calc(${height} - var(--v-app-bar-height))`;
                height = `calc(${height} - var(--v-app-bar-offset))`;
            }

            if (this.showRiskWarning) {
                height = `calc(${height} - ${this.appRiskWarningHeight})`;
            }

            if (this.showCompatibilityWarning) {
                height = `calc(${height} - ${this.appCompatibilityWarningHeight})`;
            }

            if (this.showCrumbs) {
                height = `calc(${height} - ${this.appBreadcrumbsHeight})`;
            }

            return height;
        },
        appBarOffset() {
            let offset = '0px';

            if (this.showInvestorAdditionalNeeds) {
                offset = `calc(${offset} + ${this.appInvestorAdditionalNeedsHeight})`;
            }

            if (this.showInvestorPortfolioWarning) {
                offset = `calc(${offset} + ${this.appInvestorPortfolioWarningHeight})`;
            }

            return offset;
        },
        appBarTop() {
            let top = '0px';

            if (this.showRiskWarning) {
                top = `calc(${top} + ${this.appRiskWarningHeight})`;
            }

            if (this.showCompatibilityWarning) {
                top = `calc(${top} + ${this.appCompatibilityWarningHeight})`;
            }

            return top;
        },
        appMainPaddingTop() {
            let paddingTop = '0px';

            if (this.showAppBar) {
                paddingTop = `calc(${paddingTop} + var(--v-app-bar-height) + var(--v-app-bar-offset))`;
            }

            if (this.showCrumbs) {
                paddingTop = `calc(${paddingTop} + ${this.appBreadcrumbsHeight})`;
            }

            return paddingTop;
        }
    },
    watch: {
        recategorised(v) {
            if (!v) {
                this.replaceQueryParam('recategorised');
            }
        },
        '$route.query.tcs'(tcs) {
            this.requireTermsAcceptance = !!tcs;
        },
        '$route.query.hang': {
            immediate: true,
            handler() {
                if (isPlatformLoadHangEnabled(this.$route)) {
                    this.platformLoadHangActive = true;
                }
            }
        },
        showInvestorAdditionalNeeds(v, ov) {
            this.previous.showInvestorAdditionalNeeds = ov;
        },
        showInvestorPortfolioWarning(v, ov) {
            this.previous.showInvestorPortfolioWarning = ov;
        },
        showPlatformLoadingOverlay: {
            immediate: true,
            handler(isLoading) {
                this.clearPlatformLoadTimer();

                if (!isLoading) {
                    if (this.showApp && !this.platformLoadTimedOut) {
                        this.platformLoadStartedAt = null;
                        this.platformLoadDiagnostics = null;
                    }

                    return;
                }

                if (this.platformLoadTimedOut) {
                    return;
                }

                this.platformLoadStartedAt = Date.now();
                this.platformLoadDiagnostics = null;
                this.platformLoadTimer = setTimeout(this.onPlatformLoadTimeout, this.platformLoadTimeoutMs);
            }
        }
    },
    mounted() {
        if (this.getQueryParam('recategorised')) {
            this.recategorised = true;
        }

        if (this.getQueryParam('tcs')) {
            this.requireTermsAcceptance = true;
        }

        window.addEventListener('focus', this.handleTabFocus);
        window.addEventListener('blur', this.handleTabBlur);
        window.addEventListener('keydown', this.handleKeyDown);
        window.addEventListener('keyup', this.handleKeyUp);
    },
    beforeDestroy() {
        this.clearPlatformLoadTimer();
        window.removeEventListener('focus', this.handleTabFocus);
        window.removeEventListener('blur', this.handleTabBlur);
        window.removeEventListener('keydown', this.handleKeyDown);
        window.removeEventListener('keyup', this.handleKeyUp);
    },
    methods: {
        async handleTabFocus() {
            if (this.isPublic || this.pinging) {
                return;
            }

            try {
                this.pinging = true;
                await this.Auth().ping();
                this.sessionExpired = false;
            } catch (error) {
                if (error?.response?.status === 401) {
                    this.sessionExpired = true;
                } else {
                    this.sessionExpired = false;
                }
            } finally {
                this.pinging = false;
            }
        },
        handleTabBlur() {
            this.$store.dispatch('keyboard/clear');
        },
        handleKeyDown(event) {
            this.$store.dispatch('keyboard/press', event);
        },
        handleKeyUp(event) {
            this.$store.dispatch('keyboard/release', event);
        },
        clearPlatformLoadTimer() {
            if (this.platformLoadTimer) {
                clearTimeout(this.platformLoadTimer);
                this.platformLoadTimer = null;
            }
        },
        onPlatformLoadTimeout() {
            const elapsedMs = this.platformLoadStartedAt ? Date.now() - this.platformLoadStartedAt : null;
            const simulateHang = this.platformLoadHangActive;
            const diagnostics = buildPlatformLoadDiagnostics({
                store: this.$store,
                auth: this.Auth(),
                route: this.$route,
                elapsedMs,
                simulateHang
            });

            ErrorService.logPlatformLoadTimeout(diagnostics);
            this.platformLoadDiagnostics = diagnostics;
            this.platformLoadHangActive = false;
            this.platformLoadTimedOut = true;
            this.platformLoadTimer = null;
        }
    }
};
</script>
